import type { TFunction } from 'i18next';

export const imageExtensions = [
  'gif',
  'png',
  'jpg',
  'jpeg',
  'bmp',
  'webp',
  'svg',
  'ico',
  'cur',
  'tiff',
];

export const videoExtensions = [
  'midi',
  'mp3',
  'webm',
  'ogg',
  'ogv',
  'oga',
  'ogx',
  'spx',
  'wav',
  'mp4',
];

export const documentExtensions = [
  'txt',
  'pdf',
  'json',
  'doc',
  'docx',
  'xls',
  'xlsx',
  'odt',
  'ods',
  'ppt',
  'pptx',
];

export const archiveExtensions = ['zip', 'gzip'];

export const handleExtensions = (
  currentValue: string[],
  newValue: string[],
  extensions: string[],
) => {
  const newValueSet = new Set([...currentValue, ...extensions]);
  const containsAll = newValueSet.size === currentValue.length;

  if (!containsAll) {
    newValue.push(...extensions);
    return newValue;
  } else return newValue.filter((ext) => !extensions.includes(ext));
};

export const parseExtensionFilters = (currentValue: string[]) => {
  let newValue: string[] = [];

  currentValue.forEach((extension) => {
    switch (extension) {
      case 'images': {
        newValue = handleExtensions(currentValue, newValue, imageExtensions);
        return;
      }
      case 'videos': {
        newValue = handleExtensions(currentValue, newValue, videoExtensions);
        return;
      }
      case 'documents': {
        newValue = handleExtensions(currentValue, newValue, documentExtensions);
        return;
      }
      case 'archives': {
        newValue = handleExtensions(currentValue, newValue, archiveExtensions);
        return;
      }
      default: {
        newValue.push(extension);
        return;
      }
    }
  });

  return Array.from(new Set(newValue));
};

export const getExtensionOptions = (currentValue: string[], t: TFunction) => {
  const allImagesChecked = imageExtensions.every((extension) =>
    currentValue.includes(extension),
  );
  const allVideosChecked = videoExtensions.every((extension) =>
    currentValue.includes(extension),
  );
  const allDocumentsChecked = documentExtensions.every((extension) =>
    currentValue.includes(extension),
  );
  const allArchivesChecked = archiveExtensions.every((extension) =>
    currentValue.includes(extension),
  );

  return [
    {
      group: t('Media.ExtensionOptions.Presets'),
      value: 'images',
      label: (
        <div className={allImagesChecked ? 'font-bold ' : ''}>
          {t('Media.ExtensionOptions.AllImages')}
        </div>
      ),
      ignoreCheckmark: true,
      searchString: 'images',
    },
    {
      group: t('Media.ExtensionOptions.Presets'),
      value: 'videos',
      label: (
        <div className={allVideosChecked ? 'font-bold ' : ''}>
          {t('Media.ExtensionOptions.AllVideos')}
        </div>
      ),
      ignoreCheckmark: true,
      searchString: 'videos',
    },
    {
      group: t('Media.ExtensionOptions.Presets'),
      value: 'documents',
      label: (
        <div className={allDocumentsChecked ? 'font-bold ' : ''}>
          {t('Media.ExtensionOptions.AllDocuments')}
        </div>
      ),
      ignoreCheckmark: true,
      searchString: 'documents',
    },
    {
      group: t('Media.ExtensionOptions.Presets'),
      value: 'archives',
      label: (
        <div className={allArchivesChecked ? 'font-bold ' : ''}>
          {t('Media.ExtensionOptions.AllArchives')}
        </div>
      ),
      ignoreCheckmark: true,
      searchString: 'archives',
    },
    ...[
      ...imageExtensions,
      ...videoExtensions,
      ...documentExtensions,
      ...archiveExtensions,
    ].map((extension) => ({
      value: extension,
      label: extension,
    })),
  ];
};
